import { Autocomplete, TextField as MuiTextField, InputLabel, Box, SxProps } from '@mui/material';
import { TextFieldProps } from './text-field';
import { useId } from 'react';
import { Option } from './select-field';

export type ComboBoxProps = Omit<TextFieldProps, 'onChange'> & {
    options: Option[];
    onChange?: (event: { target: { value: string } }) => void;
    value?: string;
    loading?: boolean;
    renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: Option) => React.ReactNode;
    filterOptions?: (options: Option[], state: { inputValue: string }) => Option[];
    openOnFocus?: boolean;
    freeSolo?: boolean;
    open?: boolean;
    onOpen?: () => void;
    onClose?: (event: React.SyntheticEvent, reason: string) => void;
    inputValue?: string;
    onInputChange?: (event: React.SyntheticEvent, value: string) => void;
    /**
     * Optional styles applied to the autocomplete Paper (dropdown panel).
     * Useful for controlling width/spacing of the popup separately from the input.
     */
    paperSx?: SxProps;
};

export const ComboBox = ({
    options = [],
    noMargin,
    label,
    placeholder,
    fullWidth,
    margin = 'none',
    size = 'small',
    variant,
    required,
    error,
    helperText,
    value,
    onChange,
    loading,
    readOnly,
    renderOption,
    filterOptions,
    openOnFocus,
    freeSolo,
    open,
    onOpen,
    onClose,
    inputValue,
    onInputChange,
    paperSx,
    ...props
}: ComboBoxProps) => {
    const inputId = useId();

    const handleOptionSelect = (event: React.SyntheticEvent, value: string | Option | null) => {
        if (!value || typeof value === 'string') {
            onChange?.({ target: { value: '' } });
            return;
        }
        onChange?.({ target: { value: `${value.value}` } });
    };

    const hasError = error;
    const errorText = helperText || '';

    const selectedValue = options.find(option => option.value === value) || null;

    return (
        <Autocomplete
            /*
             * UX-831: THE `id` IS WHAT MAKES THE LABEL BELOW REACH THE INPUT. `Autocomplete` puts
             * this id on the `<input>` it renders and hands it back through `renderInput`'s
             * `params`, so the `InputLabel`'s `htmlFor={inputId}` resolves to a real element.
             *
             * Without it the label pointed at an id that existed nowhere in the DOM — `Autocomplete`
             * generated its own — so the field rendered a visible label and still had NO accessible
             * name. Same shape as the `TextField` `aria-label` and `SelectField` `aria-labelledby`
             * bugs (ROBO-324): a name that looks present to a reviewer and is absent to a screen
             * reader. `combo-box-aria-label.test.tsx` asserts the association, by role and name.
             */
            id={inputId}
            freeSolo={freeSolo}
            disablePortal
            open={open}
            onOpen={onOpen}
            onClose={(event, reason) => {
                onClose?.(event, reason as string);
            }}
            openOnFocus={openOnFocus}
            options={options}
            inputValue={inputValue}
            onInputChange={onInputChange}
            getOptionLabel={option => {
                if (typeof option === 'string') return option;
                return option.label || '';
            }}
            onChange={handleOptionSelect}
            fullWidth={fullWidth}
            loading={loading}
            value={selectedValue}
            readOnly={readOnly}
            noOptionsText="No options"
            loadingText="Loading options..."
            filterOptions={filterOptions}
            componentsProps={{
                paper: {
                    sx: paperSx
                }
            }}
            renderInput={params => (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        width: '100%',
                        mb: noMargin ? 0 : 2
                    }}
                >
                    <InputLabel
                        shrink={false}
                        id={`${inputId}-label`}
                        htmlFor={inputId}
                        sx={{
                            fontSize: 12
                        }}
                        required={required}
                    >
                        {label}
                    </InputLabel>
                    <MuiTextField
                        {...props}
                        {...params}
                        placeholder={placeholder}
                        fullWidth={fullWidth}
                        size={size}
                        margin={margin}
                        variant={variant}
                        required={required}
                        error={hasError}
                        helperText={errorText}
                        sx={{
                            ...props.sx,
                            ...(readOnly && {
                                backgroundColor: 'var(--background-content, #fff)',
                                pointerEvents: 'none',
                                '& .MuiInputBase-input': {
                                    color: 'text.secondary',
                                    backgroundColor: 'var(--background-content, #fff)'
                                },
                                '& .MuiInputBase-input:focus': {
                                    outline: '0',
                                    borderColor: 'grey.100',
                                    borderWidth: '0.75px',
                                    borderStyle: 'solid'
                                }
                            })
                        }}
                    />
                </Box>
            )}
            renderOption={(props, option) =>
                renderOption ? renderOption(props, option) : <li {...props}>{option.label}</li>
            }
            sx={{
                ...(readOnly && {
                    p: 0,
                    pointerEvents: 'none',
                    '& .MuiInputBase-input': {
                        color: 'text.secondary',
                        backgroundColor: 'var(--background-content, #fff)'
                    },
                    '& .MuiInputBase-input:focus': {
                        outline: '0',
                        borderColor: 'grey.100',
                        borderWidth: '0.75px',
                        borderStyle: 'solid'
                    }
                })
            }}
        />
    );
};
